/**
 * Google One Tap (ROADMAP 1.37 items 3 and 4).
 *
 * One Tap signs a reader in with the Google account their browser already
 * holds — no form, no popup — and hands back an ID token that Firebase accepts
 * as a credential, so it is the same account the button creates, not a second
 * auth system. Google's published case studies put the lift on sign-up at
 * roughly 2x to 8x (plan §1); ours is measured by the `one_tap_*` events.
 *
 * Loaded LAZILY, at the moment it is offered: the script is Google's, and an
 * anonymous reader who is never asked should never pay for it (the lesson of
 * [[BUGS #401]] fix 3). FedCM on, as Chrome now requires; Google applies its
 * own cool-down after a dismissal, so we never chase.
 *
 * Automatic sign-in (item 4) is on only for a reader who has signed in with
 * Google here before — never for a first visit.
 */

export const GSI_SRC = 'https://accounts.google.com/gsi/client';
export const RETURNING_GOOGLE_KEY = 'ia:signedInWithGoogle';

interface GoogleIdApi {
  initialize: (config: Record<string, unknown>) => void;
  prompt: () => void;
  cancel: () => void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdApi } };
  }
}

let loading: Promise<GoogleIdApi | null> | null = null;

/** Load Google's script once; resolves to the API, or null if it failed. */
export function loadGoogleIdentity(): Promise<GoogleIdApi | null> {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  if (loading) return loading;
  loading = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google?.accounts?.id ?? null);
    script.onerror = () => { loading = null; resolve(null); };
    document.head.appendChild(script);
  });
  return loading;
}

export function isReturningGoogleUser(): boolean {
  try {
    return localStorage.getItem(RETURNING_GOOGLE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Show One Tap. `onCredential` receives the ID token when the reader accepts. */
export async function promptOneTap(
  clientId: string,
  onCredential: (idToken: string) => void,
): Promise<boolean> {
  const api = await loadGoogleIdentity();
  if (!api) return false;
  api.initialize({
    client_id: clientId,
    callback: (response: { credential?: string }) => {
      if (response?.credential) onCredential(response.credential);
    },
    auto_select: isReturningGoogleUser(),
    cancel_on_tap_outside: true,
    context: 'signup',
    itp_support: true,
    use_fedcm_for_prompt: true,
  });
  api.prompt();
  return true;
}

export function cancelOneTap(): void {
  try {
    window.google?.accounts?.id?.cancel();
  } catch {
    // Nothing shown, nothing to cancel.
  }
}
